import {
  BARCODE_TYPES,
  barcodeContent,
  barcodeDrawing,
  isTwoDimensional,
  withSvgSize,
  type BarcodeType,
} from "@neus/shared";

declare const __BARCODE_TYPE__: BarcodeType;

/** The barcode type the store's scanners read (BARCODE_TYPE at build time, checked in vite.config.ts). */
export const barcodeType: BarcodeType = __BARCODE_TYPE__;
export const barcodeLabel = BARCODE_TYPES[barcodeType].label;
export const twoDimensional = isTwoDimensional(barcodeType);

// bwip-js loads only on the pages that draw codes (staff orders, a shopper's pickup order), so it stays out of the
// main bundle.
const loadBwip = () => import("bwip-js/browser");

/**
 * How pages query for codes. A code never changes and needs no network once bwip-js has loaded, and a query with no
 * data counts as stale whatever its staleTime, so without these a failure would clear itself on every window focus.
 */
export const CODE_QUERY = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  networkMode: "always",
} as const;

/** A product's barcode as SVG markup, or undefined when it can't have one of this type (a GS1 type and no GTIN). */
export async function productBarcode(product: { sku: string; gtin?: string }): Promise<string | undefined> {
  const content = barcodeContent(barcodeType, product);
  if (!content) return undefined;
  const { toSVG } = await loadBwip();
  try {
    return withSvgSize(toSVG({ ...content, ...barcodeDrawing(barcodeType) }));
  } catch {
    // bwip-js refuses data its symbology can't hold: no barcode rather than a broken page.
    return undefined;
  }
}

/** A QR code for a link, as SVG markup. */
export async function qrCode(url: string): Promise<string> {
  const { toSVG } = await loadBwip();
  // Error correction M, the BWIPP default.
  return toSVG({ bcid: "qrcode", text: url, scale: 2 });
}

/** A QR code for a link as a PNG, to share as an image (a text message can't carry SVG). */
export async function qrPng(url: string): Promise<Blob> {
  const { toCanvas } = await loadBwip();
  const canvas = document.createElement("canvas");
  toCanvas(canvas, {
    bcid: "qrcode",
    text: url,
    scale: 8,
    paddingwidth: 4,
    paddingheight: 4,
    backgroundcolor: "FFFFFF",
  });
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Couldn't draw the QR code."))), "image/png"),
  );
}
